import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, eur, fmtDate, fmtDateTime, KIND_LABELS, REFUND_MSG } from "@/lib/api";
import { PageShell } from "@/components/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import { CatalogTable } from "@/components/admin/CatalogTable";
import { AuthzUsers, Verifications, Policies } from "@/components/admin/AuthzPanel";
import { OperatorsApproval } from "@/components/admin/OperatorsApproval";
import { PartnersAdmin } from "@/components/admin/PartnersAdmin";
import { ServicesAdmin } from "@/components/admin/ServicePricing";

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data));
  }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="admin-stats">
      {Object.entries(s).map(([k, v]) => (
        <div key={k} className="glass rounded-2xl p-4" data-testid={`stat-${k}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-700">{k.replace(/_/g, " ")}</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-slate-900">{k.includes("incasso") ? eur(v) : v}</p>
        </div>
      ))}
    </div>
  );
}

function Bookings() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/appointments").then(({ data }) => setItems(data));
  }, []);
  return (
    <div className="space-y-2" data-testid="admin-bookings">
      {items.map((a) => <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs"><span>{a.patient_name} · {KIND_LABELS[a.kind]} · {a.service_name} · {a.professional_name} · {fmtDate(a.date)} {a.time || a.time_band} · {eur(a.amount)}</span><StatusBadge status={a.status} /></div>)}
      {!items.length && <p className="text-sm text-slate-500">Nessuna prenotazione.</p>}
    </div>
  );
}

function AdminPayments() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/payments/admin/all").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const refund = async (p) => {
    try {
      const { data } = await api.post(`/payments/admin/${p.id}/refund`);
      toast.success(REFUND_MSG[data.refund_mode] || "Rimborso registrato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2" data-testid="admin-payments">
      <p className="text-xs font-semibold text-amber-700">Provider pagamenti: Stripe (modalità di prova).</p>
      {items.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs">
          <span>{p.receipt_number || p.id} · {p.description} · {eur(p.amount)} · {fmtDateTime(p.created_at)}</span>
          <span className="flex items-center gap-2"><StatusBadge status={p.status} />{["pagato", "rimborsato_parziale"].includes(p.status) && <Button size="sm" variant="outline" data-testid={`admin-refund-${p.id}`} onClick={() => window.confirm(`Rimborsare ${eur(p.amount - (p.refunded_amount || 0))} al paziente? Con Stripe il denaro viene restituito sulla carta.`) && refund(p)}>Rimborsa</Button>}</span>
        </div>
      ))}
    </div>
  );
}

function SelvixConfig() {
  const [c, setC] = useState(null);
  const [models, setModels] = useState({});
  useEffect(() => {
    api.get("/settings/selvix").then(({ data }) => setC({ ai_model: "openai:gpt-6-luna", ...data }));
    api.get("/settings/ai-models").then(({ data }) => setModels(data));
  }, []);
  if (!c) return null;
  const save = async () => {
    try {
      await api.put("/admin/settings/selvix", c);
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="glass max-w-2xl space-y-3 rounded-3xl p-5" data-testid="selvix-config">
      <Label>Modello AI di Doctor Selvix</Label>
      <select data-testid="selvix-ai-model-select" value={c.ai_model} onChange={(e) => setC({ ...c, ai_model: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
        {Object.entries(models).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Label>Messaggio di benvenuto</Label>
      <Textarea data-testid="selvix-welcome-input" rows={4} value={c.welcome_message} onChange={(e) => setC({ ...c, welcome_message: e.target.value })} />
      <Label>Istruzioni aggiuntive per l'AI</Label>
      <Textarea data-testid="selvix-instructions-input" value={c.extra_instructions} onChange={(e) => setC({ ...c, extra_instructions: e.target.value })} />
      <Label>URL modello 3D GLB approvato (vuoto = PNG originale)</Label>
      <Input data-testid="selvix-model-input" value={c.avatar_model_url} onChange={(e) => setC({ ...c, avatar_model_url: e.target.value })} />
      <Label>Numero emergenze</Label>
      <Input value={c.emergency_number} onChange={(e) => setC({ ...c, emergency_number: e.target.value })} />
      <label className="flex items-center gap-2 text-sm"><Switch checked={c.voice_enabled} onCheckedChange={(v) => setC({ ...c, voice_enabled: v })} /> Sintesi vocale</label>
      <Button data-testid="selvix-config-save" onClick={save} className="rounded-full bg-sky-600 hover:bg-sky-700">Salva</Button>
    </div>
  );
}

function Audit() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/audit").then(({ data }) => setItems(data));
  }, []);
  return <div className="max-h-[60vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3 font-mono text-[11px] text-slate-600" data-testid="admin-audit">{items.map((a) => <p key={a.id}>{fmtDateTime(a.at)} · {a.actor_email || "sistema"} · {a.action} · {a.target} {a.detail}</p>)}</div>;
}

const TABS = [
  ["panoramica", "Panoramica", <Stats />], ["utenti", "Utenti e autorizzazioni", <AuthzUsers />], ["operatori", "Nuovi operatori", <OperatorsApproval />], ["verifiche", "Verifiche professionali", <Verifications />],
  ["policy", "Requisiti servizi e permessi", <Policies />], ["reparti", "Reparti e stanze 3D", <CatalogTable collection="departments" />],
  ["servizi", "Servizi e tariffe", <ServicesAdmin />], ["professionisti", "Professionisti e disponibilità", <CatalogTable collection="professionals" />],
  ["contenuti", "Contenuti informativi", <CatalogTable collection="content" />], ["prenotazioni", "Prenotazioni", <Bookings />],
  ["pagamenti", "Pagamenti", <AdminPayments />], ["selvix", "Doctor Selvix", <SelvixConfig />], ["partner", "Partner", <PartnersAdmin />], ["audit", "Audit log", <Audit />],
];

export default function AdminPage() {
  return (
    <PageShell title="Pannello amministrativo" subtitle="Gestisci clinica virtuale, servizi, professionisti, autorizzazioni e configurazione di Doctor Selvix." testid="admin-page">
      <Tabs defaultValue="panoramica">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        {TABS.map(([k, , el]) => <TabsContent key={k} value={k}>{el}</TabsContent>)}
      </Tabs>
    </PageShell>
  );
}
